import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ComponentType, ItemStatus } from '../enums';
import { 
  Building,
  Custodian,
  Item, 
  ItemExtended,
  Part,
  Project,
  PurchaseOrderExtended,
  Room,
  ShippingOrderExtended,
  Site,
  SubCustodyAssignment,
  User,
  Manufacturer,
  Vendor
} from '../models';
import { BuildingsService } from '../services/buildings.service';
import { CustodiansService } from '../services/custodians.service';
import { ItemsService } from '../services/items.service';
import { ManufacturersService } from '../services/manufacturers.service';
import { PartsService } from '../services/parts.service';
import { ProjectsService } from '../services/projects.service';
import { PurchaseOrdersService } from '../services/purchase-orders.service';
import { ReportsService } from '../services/reports.service';
import { RipsService } from '../services/rips.service';
import { RoomsService } from '../services/rooms.service';
import { ShippingLocationsService } from '../services/shipping-locations.service';
import { ShippingOrdersService } from '../services/shipping-orders.service';
import { SitesService } from '../services/sites.service';
import { SubCustodyAssignmentsService } from '../services/sub-custody-assignments.service';
import { UsersService } from '../services/users.service';
import { VendorsService } from '../services/vendors.service';

export interface ItemHistory {
  item: Item;
  periodStart: string;
  periodEnd: string;
}

export interface ItemChangeSummary {
  changes: ItemChange[];
  version: number;
  periodStart: string;
  periodEnd: string;
  changedBy: string;
  changedByEmail?: string;
  changeDate: string;
  itemIdentifier: string;  // SMX ID or other unique identifier
  itemDescription: string; // Part number + description
  changeType: ChangeType;
  metadata: AuditMetadata;
}

export interface AuditMetadata {
  site: string;
  building: string;
  room: string;
  project: string;
  purchaseOrder: string;
  status: string;
}

export enum ChangeType {
  Location = 'Location Change',
  Assignment = 'Assignment Change',
  Status = 'Status Change',
  Properties = 'Property Change',
  Identification = 'Identification Change',
  LastInventoriedDate = 'Last Inventoried Date Change'
}

interface ItemChange {
  field: string;
  oldValue: string;
  newValue: string;
  category: string;
}

interface FieldMapping {
  displayName: string;
  category: string;
  valueResolver?: (value: any, context: ChangeContext) => string;
}

interface ChangeContext {
  items: ItemExtended[];
  custodians: Custodian[];
  subCustodyAssignments: SubCustodyAssignment[];
  buildings: Building[];
  rooms: Room[];
  sites: Site[];
  parts: Part[];
  projects: Project[];
  purchaseOrders: PurchaseOrderExtended[];
  shippingOrders: ShippingOrderExtended[];
  users: { [key: number]: User };
  manufacturers: Manufacturer[];
  vendors: Vendor[];
}

type ServiceResponse<T> = Observable<T[]>;
type UserMapResponse = Observable<{ [key: number]: User }>;

@Injectable({ providedIn: 'root' })
export class ItemHistoryFactory {
  private readonly fieldMappings: { [key: string]: FieldMapping } = {
    assignedToId: {
      displayName: 'Assigned To',
      category: 'Assignment',
      valueResolver: (value, context) => {
        const custodian = context.custodians.find(c => c.id === value);
        return custodian ? `${custodian.firstName} ${custodian.lastName}` : value?.toString() || '-';
      }
    },
    buildingId: {
      displayName: 'Building',
      category: 'Location',
      valueResolver: (value, context) => {
        const building = context.buildings.find(b => b.id === value);
        return building ? building.name : value?.toString() || '-';
      }
    },
    cci: {
      displayName: 'CCI',
      category: 'Properties',
      valueResolver: (value) => value ? 'Yes' : 'No'
    },
    classification: { 
      displayName: 'Classification',
      category: 'Properties'
    },
    comments: { 
      displayName: 'Comments',
      category: 'Properties'
    },
    componentType: { 
      displayName: 'Component Type',
      category: 'Properties',
      valueResolver: (value) => value || '-'
    },
    conditionCode: { 
      displayName: 'Condition Code',
      category: 'Properties'
    },
    hsCode: { 
      displayName: 'HS Code',
      category: 'Properties'
    },
    itarEccn: { 
      displayName: 'ITAR/ECCN',
      category: 'Properties'
    },
    location: { 
      displayName: 'Location',
      category: 'Location'
    },
    modelNumber: { 
      displayName: 'Model Number',
      category: 'Identification'
    },
    nhaId: {
      displayName: 'Next Higher Assembly',
      category: 'Properties',
      valueResolver: (value, context) => {
        const item = context.items.find(i => i.id === value);
        return item ? item.smxId : value?.toString() || '-';
      }
    },
    orderQuantity: { 
      displayName: 'Order Quantity',
      category: 'Properties'
    },
    partId: {
      displayName: 'Part',
      category: 'Identification',
      valueResolver: (value, context) => {
        const part = context.parts.find(p => p.id === value);
        return part ? `${part.partNumber} - ${part.description}` : value?.toString() || '-';
      }
    },
    projectId: {
      displayName: 'Project',
      category: 'Properties',
      valueResolver: (value, context) => {
        const project = context.projects.find(p => p.id === value);
        return project ? project.title : value?.toString() || '-';
      }
    },
    purchaseOrderId: {
      displayName: 'Purchase Order',
      category: 'Properties',
      valueResolver: (value, context) => {
        const po = context.purchaseOrders.find(p => p.id === value);
        return po ? po.number : value?.toString() || '-';
      }
    },
    roomId: {
      displayName: 'Room',
      category: 'Location',
      valueResolver: (value, context) => {
        const room = context.rooms.find(r => r.id === value);
        return room ? room.name : value?.toString() || '-';
      }
    },
    serialNumber: { 
      displayName: 'Serial Number',
      category: 'Identification'
    },
    shippingOrderId: {
      displayName: 'Shipping Order',
      category: 'Properties',
      valueResolver: (value, context) => {
        const order = context.shippingOrders.find(s => s.id === value);
        return order ? order.number : value?.toString() || '-';
      }
    },
    siteId: {
      displayName: 'Site',
      category: 'Location',
      valueResolver: (value, context) => {
        const site = context.sites.find(s => s.id === value);
        return site ? site.name : value?.toString() || '-';
      }
    },
    smxId: { 
      displayName: 'SMX ID',
      category: 'Identification'
    },
    status: { 
      displayName: 'Status',
      category: 'Status'
    },
    subCustodyAssignmentId: {
      displayName: 'Sub-Custody Assignment',
      category: 'Assignment',
      valueResolver: (value, context) => {
        const assignment = context.subCustodyAssignments.find(s => s.id === value);
        if (!assignment) return value?.toString() || '-';
        const custodian = context.custodians.find(c => c.id === assignment.custodianId);
        return custodian ? `${custodian.firstName} ${custodian.lastName}` : value?.toString() || '-';
      }
    },
    totalCost: {
      displayName: 'Total Cost',
      category: 'Properties',
      valueResolver: (value) => value ? `$${Number(value).toFixed(2)}` : '-'
    },
    unitPrice: {
      displayName: 'Unit Price',
      category: 'Properties',
      valueResolver: (value) => value ? `$${Number(value).toFixed(2)}` : '-'
    },
    warrantyExp: {
      displayName: 'Warranty Expiration',
      category: 'Properties',
      valueResolver: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },
    nsn: {
      displayName: 'NSN',
      category: 'Identification'
    },
    quantity: {
      displayName: 'Quantity',
      category: 'Properties',
      valueResolver: (value) => value?.toString() || '-'
    },
    manufacturer: {
      displayName: 'Manufacturer',
      category: 'Properties'
    },
    manufacturerId: {
      displayName: 'Manufacturer',
      category: 'Properties',
      valueResolver: (value, context) => {
        const manufacturer = context.manufacturers.find(m => m.id === value);
        return manufacturer ? manufacturer.name : value?.toString() || '-';
      }
    },
    vendor: {
      displayName: 'Vendor',
      category: 'Properties'
    },
    vendorId: {
      displayName: 'Vendor',
      category: 'Properties',
      valueResolver: (value, context) => {
        const vendor = context.vendors.find(v => v.id === value);
        return vendor ? vendor.name : value?.toString() || '-';
      }
    },
    lastInventoriedDate: {
      displayName: 'Last Inventory Date',
      category: ChangeType.LastInventoriedDate,
      valueResolver: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },
    lastInventoryBy: {
      displayName: 'Last Inventory By',
      category: 'Properties',
      valueResolver: (value, context) => {
        const user = context.users[value];
        return user ? `${user.firstName} ${user.lastName}` : value?.toString() || '-';
      }
    },
    lastCalibrationDate: {
      displayName: 'Last Calibration Date',
      category: 'Properties',
      valueResolver: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },
    nextCalibrationDate: {
      displayName: 'Next Calibration Date',
      category: 'Properties',
      valueResolver: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },
    calibrationFrequency: {
      displayName: 'Calibration Frequency',
      category: 'Properties'
    },
    calibrationVendor: {
      displayName: 'Calibration Vendor',
      category: 'Properties'
    },
    calibrationProcedure: {
      displayName: 'Calibration Procedure',
      category: 'Properties'
    },
    calibrationNotes: {
      displayName: 'Calibration Notes',
      category: 'Properties'
    },
    maintenanceNotes: {
      displayName: 'Maintenance Notes',
      category: 'Properties'
    },
    maintenanceSchedule: {
      displayName: 'Maintenance Schedule',
      category: 'Properties'
    },
    lastMaintenanceDate: {
      displayName: 'Last Maintenance Date',
      category: 'Properties',
      valueResolver: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },
    nextMaintenanceDate: {
      displayName: 'Next Maintenance Date',
      category: 'Properties',
      valueResolver: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },
    assignedDate: {
      displayName: 'Assigned Date',
      category: 'Assignment',
      valueResolver: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },
    projectCode: {
      displayName: 'Project Code',
      category: 'Properties'
    },
    projectContractNumber: {
      displayName: 'Contract Number',
      category: 'Properties'
    },
    projectTdlNumber: {
      displayName: 'TDL Number',
      category: 'Properties'
    },
    partUnitOfIssue: {
      displayName: 'Unit of Issue',
      category: 'Properties'
    },
    partNsn: {
      displayName: 'NSN',
      category: 'Identification'
    },
    shippingLocationName: {
      displayName: 'Shipping Location',
      category: 'Location'
    },
    shippingLocationAddress1: {
      displayName: 'Shipping Address 1',
      category: 'Location'
    },
    shippingLocationAddress2: {
      displayName: 'Shipping Address 2',
      category: 'Location'
    },
    shippingLocationCity: {
      displayName: 'Shipping City',
      category: 'Location'
    },
    shippingLocationState: {
      displayName: 'Shipping State',
      category: 'Location'
    },
    shippingLocationZip: {
      displayName: 'Shipping ZIP',
      category: 'Location'
    },
    purchaseOrderDeliveryDate: {
      displayName: 'PO Delivery Date',
      category: 'Properties',
      valueResolver: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },
    purchaseOrderOrderDate: {
      displayName: 'PO Order Date',
      category: 'Properties',
      valueResolver: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },
    purchaseOrderShippingDate: {
      displayName: 'PO Shipping Date',
      category: 'Properties',
      valueResolver: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },
    purchaseOrderStatus: {
      displayName: 'PO Status',
      category: 'Properties'
    },
    purchaseOrderTrackingNumber: {
      displayName: 'PO Tracking Number',
      category: 'Properties'
    },
    purchaseOrderVendorOrderNumber: {
      displayName: 'Vendor Order Number',
      category: 'Properties'
    },
    purchaseOrderNotes: {
      displayName: 'PO Notes',
      category: 'Properties'
    },
    buildingName: {
      displayName: 'Building Name',
      category: 'Location'
    },
    manufacturerName: {
      displayName: 'Manufacturer Name',
      category: 'Properties'
    },
    nhaSmxId: {
      displayName: 'NHA SMX ID',
      category: 'Properties'
    },
    partDescription: {
      displayName: 'Part Description',
      category: 'Identification'
    },
    partNumber: {
      displayName: 'Part Number',
      category: 'Identification'
    },
    projectTitle: {
      displayName: 'Project Title',
      category: 'Properties'
    },
    roomName: {
      displayName: 'Room Name',
      category: 'Location'
    },
    siteName: {
      displayName: 'Site Name',
      category: 'Location'
    },
    vendorName: {
      displayName: 'Vendor Name',
      category: 'Properties'
    }
  };

  constructor(
    private buildingsService: BuildingsService,
    private custodiansService: CustodiansService,
    private itemsService: ItemsService,
    private manufacturersService: ManufacturersService,
    private partsService: PartsService,
    private projectsService: ProjectsService,
    private purchaseOrdersService: PurchaseOrdersService,
    private reportsService: ReportsService,
    private ripsService: RipsService,
    private roomsService: RoomsService,
    private shippingLocationsService: ShippingLocationsService,
    private shippingOrdersService: ShippingOrdersService,
    private sitesService: SitesService,
    private subCustodyAssignmentsService: SubCustodyAssignmentsService,
    private usersService: UsersService,
    private vendorsService: VendorsService,
  ) {}

  private determineChangeType(changes: ItemChange[]): ChangeType {
    const categories = new Set(changes.map(c => c.category));
    
    if (categories.has('Location')) return ChangeType.Location;
    if (categories.has('Assignment')) return ChangeType.Assignment;
    if (categories.has('Status')) return ChangeType.Status;
    if (categories.has('Identification')) return ChangeType.Identification;
    if (categories.has('LastInventoriedDate')) return ChangeType.LastInventoriedDate;
    return ChangeType.Properties;
  }

  private getAuditMetadata(item: Item, context: ChangeContext): AuditMetadata {
    const site = context.sites.find(s => s.id === item.siteId);
    const building = context.buildings.find(b => b.id === item.buildingId);
    const room = context.rooms.find(r => r.id === item.roomId);
    const project = context.projects.find(p => p.id === item.projectId);
    const po = context.purchaseOrders.find(p => p.id === item.purchaseOrderId);

    return {
      site: site?.name || '-',
      building: building?.name || '-',
      room: room?.name || '-',
      project: project?.title || '-',
      purchaseOrder: po?.number || '-',
      status: item.status || '-'
    };
  }

  public getChanges(versions: ItemHistory[]): Observable<ItemChangeSummary[]> {
    const items$ = this.itemsService.entities$ as ServiceResponse<ItemExtended>;
    const users$ = this.usersService.entityMap$ as UserMapResponse;
    const custodians$ = this.custodiansService.entities$ as ServiceResponse<Custodian>;
    const subCustodyAssignments$ = this.subCustodyAssignmentsService.entities$ as ServiceResponse<SubCustodyAssignment>;
    const buildings$ = this.buildingsService.entities$ as ServiceResponse<Building>;
    const rooms$ = this.roomsService.entities$ as ServiceResponse<Room>;
    const sites$ = this.sitesService.entities$ as ServiceResponse<Site>;
    const parts$ = this.partsService.entities$ as ServiceResponse<Part>;
    const projects$ = this.projectsService.entities$ as ServiceResponse<Project>;
    const purchaseOrders$ = this.purchaseOrdersService.entities$ as ServiceResponse<PurchaseOrderExtended>;
    const shippingOrders$ = this.shippingOrdersService.entities$ as ServiceResponse<ShippingOrderExtended>;
    const manufacturers$ = this.manufacturersService.entities$ as ServiceResponse<Manufacturer>;
    const vendors$ = this.vendorsService.entities$ as ServiceResponse<Vendor>;

    return combineLatest<[
      ItemExtended[],
      { [key: number]: User },
      Custodian[],
      SubCustodyAssignment[],
      Building[],
      Room[],
      Site[],
      Part[],
      Project[],
      PurchaseOrderExtended[],
      ShippingOrderExtended[],
      Manufacturer[],
      Vendor[]
    ]>([
      items$,
      users$,
      custodians$,
      subCustodyAssignments$,
      buildings$,
      rooms$,
      sites$,
      parts$,
      projects$,
      purchaseOrders$,
      shippingOrders$,
      manufacturers$,
      vendors$
    ]).pipe(
      map(([items, users, custodians, subCustodyAssignments, buildings, rooms, sites, parts, projects, purchaseOrders, shippingOrders, manufacturers, vendors]) => {
        const context: ChangeContext = {
          items,
          custodians,
          subCustodyAssignments,
          buildings,
          rooms,
          sites,
          parts,
          projects,
          purchaseOrders,
          shippingOrders,
          users,
          manufacturers,
          vendors
        };

        const itemChangeSummaries: ItemChangeSummary[] = [];
        // Reverse versions array to process from newest to oldest
        const reversedVersions = [...versions].reverse();
        const totalVersions = reversedVersions.length - 1;
        
        // Process versions in reverse order (newest to oldest)
        for (let i = 0; i < reversedVersions.length - 1; i++) {
          const newVersion = reversedVersions[i];
          const oldVersion = reversedVersions[i + 1];
          const changes = this.findChanges(oldVersion.item, newVersion.item, context);
          const changedByUser = users[newVersion.item.updatedBy];
          if (changes.length > 0) {
            const part = parts.find(p => p.id === oldVersion.item.partId);
            itemChangeSummaries.push({
              changes,
              version: totalVersions - i,
              periodStart: newVersion.periodStart,
              periodEnd: newVersion.periodEnd,
              changedBy: `${changedByUser.firstName} ${changedByUser.lastName}`,
              changedByEmail: changedByUser.username,
              changeDate: new Date(newVersion.periodStart).toLocaleString(),
              itemIdentifier: oldVersion.item.smxId || `ID: ${oldVersion.item.id}`,
              itemDescription: part ? `${part.partNumber} - ${part.description}` : '-',
              changeType: this.determineChangeType(changes),
              metadata: this.getAuditMetadata(oldVersion.item, context)
            });
          }
        }
        return itemChangeSummaries;
      }),
    );
  }

  private findChanges(oldItem: Item, newItem: Item, context: ChangeContext) {
    const changes: ItemChange[] = [];
    const fieldsToIgnore = ['createdBy', 'createdTime', 'updatedBy', 'updatedTime'];
    
    Object.entries(oldItem).forEach(([key, value]) => {
      if (!fieldsToIgnore.includes(key)) {
        let hasChanged = false;
        
        // Special handling for date fields
        if (key === 'lastInventoriedDate') {
          const oldDate = value ? new Date(value).getTime() : null;
          const newDate = newItem[key] ? new Date(newItem[key]).getTime() : null;
          hasChanged = oldDate !== newDate;
        } else {
          hasChanged = value !== newItem[key];
        }

        if (hasChanged) {
          const mapping = this.fieldMappings[key];
          if (mapping) {
            const resolveValue = (val: any) => {
              if (mapping.valueResolver) {
                return mapping.valueResolver(val, context);
              }
              return val?.toString() || '-';
            };

            changes.push({
              field: mapping.displayName,
              oldValue: resolveValue(oldItem[key]),
              newValue: resolveValue(newItem[key]),
              category: mapping.category
            });
          }
        }
      }
    });
    return changes;
  }
}
